import { createContext, useContext, useMemo } from "react";
import { createTheme, type Theme } from "@mui/material";

import type { BrandingConfig } from "@utilifeed/config/branding";
import { DEFAULT_BRANDING } from "@utilifeed/config/branding";

export type AppIdentifier = "propai" | "platform";

export type BrandingContextValue = {
  branding: BrandingConfig;
  theme: Theme;
  app: AppIdentifier;
  isLoading: boolean;
  error: Error | null;
};

export const BrandingContext = createContext<BrandingContextValue | null>(null);

/** Default context value used when no BrandingProvider is present (e.g. platform-web). */
const DEFAULT_CONTEXT: BrandingContextValue = {
  branding: DEFAULT_BRANDING,
  theme: createTheme(),
  app: "platform",
  isLoading: false,
  error: null,
};

export function useBrandingContext(): BrandingContextValue {
  const context = useContext(BrandingContext);
  return context ?? DEFAULT_CONTEXT;
}

export function useAppIdentifier(): AppIdentifier {
  return useBrandingContext().app;
}

/** Returns graph colors as an array, matching the order from the branding config. */
export function useGraphColors(): string[] {
  const { branding } = useBrandingContext();
  return useMemo(() => Object.values(branding.graphColors), [branding.graphColors]);
}
